/**
 * Arctic snow on built things. The M0 look-dev rule: snow on a big top is INSET PATCHES that
 * leave the structure's own material showing round them - a plain white roof reads as a
 * blank rectangle against snow. Only small tops (bags, crates, stubs) are simply dusted.
 */

import type { Color } from 'three';
import type { Palette } from '../palette';
import { type MeshBuilder, place } from './MeshBuilder';

/** Small tops (bags, crates, barrels) are simply dusted white in the arctic. */
export function capColor(p: Palette, otherwise: string): Color {
  return p.color(p.biome === 'arctic' ? 'ground.hi' : otherwise);
}

/**
 * Snow lying on a big flat top: an inset slab, so a rim of the structure's own material
 * stays visible around it. A roof that is simply coloured white reads as a blank rectangle.
 */
export function snowSlab(mb: MeshBuilder, p: Palette, w: number, d: number, y: number, inset = 0.32): void {
  if (p.biome !== 'arctic') return;
  const sw = w - inset * 2;
  const sd = d - inset * 2;
  const snow = { all: p.color('ground.2'), top: p.color('ground.3') };
  // Three overlapping drifts rather than one sheet: wind-scoured patches show the roof.
  mb.box(sw * 0.62, 0.16, sd * 0.7, snow, place(-sw * 0.19, y, -sd * 0.15));
  mb.box(sw * 0.5, 0.2, sd * 0.5, snow, place(sw * 0.22, y, sd * 0.2));
  mb.box(sw * 0.3, 0.12, sd * 0.34, snow, place(sw * 0.3, y, -sd * 0.3));
}

/** A pitched roof as `MeshBuilder.gable` takes it: ridge along x, eaves at height `y`. */
export interface GableRoof {
  w: number;
  h: number;
  d: number;
  y: number;
}

/**
 * Snow on a pitched roof: smaller gables of the same pitch sitting just proud of the slope,
 * so eaves, ridge ends and the gaps between patches stay roof-coloured. Each patch is
 * `[centre x, width, k]` where `k` (0..1) is how far down the slope it reaches.
 */
export function snowOnGable(
  mb: MeshBuilder,
  p: Palette,
  roof: GableRoof,
  patches: ReadonlyArray<readonly [cx: number, w: number, k: number]>,
  cz = 0,
): void {
  if (p.biome !== 'arctic') return;
  for (const [cx, w, k] of patches) {
    const y = roof.y + roof.h * (1 - k) + 0.08;
    mb.gable(w, roof.h * k, roof.d * k, p.color('ground.3'), place(cx, y, cz), p.color('ground.2'));
  }
}
